import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, formatDate, formatMonth, COLORS, tooltipStyle } from '../types';

interface AiLensTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'lens-overview', 'lens-mode-breakdown', 'lens-scans-daily', 'lens-scans-monthly',
  'lens-top-scanned', 'lens-chat-usage', 'lens-diary-usage', 'lens-shares',
  'lens-shared-opens', 'lens-onboarding', 'lens-paywall', 'lens-daily-limits',
  'lens-medication', 'lens-my-health', 'lens-palm-kundli', 'subscription-funnel',
];

const MODE_COLORS: Record<string, string> = {
  food: '#f97316', medicine: '#3b82f6', plant: '#22c55e', repair: '#d97706',
  hygiene: '#eab308', product: '#a855f7', animal: '#14b8a6', document: '#6366f1',
};

const AiLensTab: React.FC<AiLensTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const lens = data['lens-overview'] || {};
  const modeBreakdown = data['lens-mode-breakdown'] || [];
  const scanDaily = data['lens-scans-daily'] || [];
  const scanMonthly = data['lens-scans-monthly'] || [];
  const topScanned = data['lens-top-scanned'] || [];
  const chatUsage = data['lens-chat-usage'] || {};
  const diaryUsage = data['lens-diary-usage'] || {};
  const shares = data['lens-shares'] || {};
  const sharedOpens = data['lens-shared-opens'] || {};
  const onboarding = data['lens-onboarding'] || {};
  const paywall = data['lens-paywall'] || {};
  const dailyLimits = data['lens-daily-limits'] || {};
  const medication = data['lens-medication'] || {};
  const myHealth = data['lens-my-health'] || {};
  const palmKundli = data['lens-palm-kundli'] || {};

  // Format daily scans for stacked bar chart
  const allModes = Array.from(new Set(modeBreakdown.map((m: any) => m.mode))) as string[];
  const dailyForChart = scanDaily.map((d: any) => ({
    date: formatDate(d.date),
    total: d.count,
    ...(d.byMode || {}),
  }));

  // Share destinations for pie chart
  const shareDests = Object.entries(shares.byDest || {}).map(([dest, count]) => ({
    name: dest.replace('_', ' '),
    value: count as number,
  }));

  // Paywall funnel
  const funnelData = [
    { step: 'Views', value: paywall.views || 0 },
    { step: 'Checkout Start', value: paywall.checkoutStarts || 0 },
    { step: 'Success', value: paywall.successes || 0 },
    { step: 'Failed', value: paywall.fails || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Row 1: Key metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Scans" value={lens.totalScans || 0} subtitle={`${lens.todayScans || 0} today`} />
        <StatCard title="Unique Scan Users" value={lens.uniqueScanUsers || 0} />
        <StatCard title="Chat Messages" value={lens.totalChatMessages || 0} subtitle={`${lens.uniqueChatUsers || 0} users chatted`} />
        <StatCard title="AI Cost" value={`$${(lens.totalAiCost || 0).toFixed(2)}`} subtitle="Total estimated" />
      </div>

      {/* Row 2: Feature usage */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Food Diary Entries" value={diaryUsage.totalEntries || 0} subtitle={`${diaryUsage.uniqueUsers || 0} users, ${diaryUsage.conversionRate || 0}% from scans`} />
        <StatCard title="Scans Shared" value={lens.totalShared || 0} />
        <StatCard title="Med Reminders Set" value={medication.remindersSet || 0} subtitle={`${medication.uniqueUsers || 0} unique users`} />
        <StatCard title="Daily Limit Hits" value={dailyLimits.totalHits || 0} subtitle={`${dailyLimits.uniqueUsers || 0} unique users`} />
      </div>

      {/* Row 3: Onboarding + Subscriptions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Onboarding Started" value={onboarding.started || lens.onboardingTotal || 0} />
        <StatCard title="Onboarding Completed" value={onboarding.completed || lens.onboardingComplete || 0}
          subtitle={`${(onboarding.started || lens.onboardingTotal) > 0
            ? ((((onboarding.completed || lens.onboardingComplete) / (onboarding.started || lens.onboardingTotal || 1)) * 100).toFixed(1))
            : 0}% completion`} />
        <StatCard title="Shared Link Opens" value={sharedOpens.total || 0} subtitle={`${sharedOpens.uniqueUsers || 0} unique, ${sharedOpens.saves || 0} saved`} />
        <StatCard title="Paywall Views" value={paywall.views || 0}
          subtitle={`${paywall.successes || 0} converted (${paywall.views > 0 ? ((paywall.successes / paywall.views) * 100).toFixed(1) : 0}%)`} />
      </div>

      {/* Tier breakdown */}
      {lens.tierBreakdown && lens.tierBreakdown.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {lens.tierBreakdown.map((t: any) => (
            <StatCard key={t.tier} title={`${t.tier.charAt(0).toUpperCase() + t.tier.slice(1)} Users`} value={t.count} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mode breakdown pie */}
        <ChartCard title="Scans by Lens Mode">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={modeBreakdown} dataKey="count" nameKey="mode" cx="50%" cy="50%" outerRadius={100} label={({ mode, pct }: any) => `${mode} ${pct}%`}>
                {modeBreakdown.map((m: any, i: number) => (
                  <Cell key={m.mode} fill={MODE_COLORS[m.mode] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Share destinations */}
        <ChartCard title="Share Destinations (30d)">
          {shareDests.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={shareDests} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }: any) => `${name}: ${value}`}>
                  {shareDests.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">No share events tracked yet</div>
          )}
        </ChartCard>
      </div>

      {/* Daily scans chart */}
      <ChartCard title="Daily Scans (Last 30 Days)">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={dailyForChart}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {allModes.map((mode, i) => (
              <Bar key={mode} dataKey={mode} stackId="scans" fill={MODE_COLORS[mode] || COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Monthly scans */}
      <ChartCard title="Monthly Scans">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={scanMonthly.map((m: any) => ({ month: formatMonth(m.year, m.month), count: m.count }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Chat usage daily */}
        <ChartCard title="Ask AI Chat Usage (30d)">
          {chatUsage.daily && chatUsage.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chatUsage.daily.map((d: any) => ({ date: formatDate(d.date), messages: d.messages, users: d.uniqueUsers }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="messages" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} name="Messages" />
                <Area type="monotone" dataKey="users" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} name="Unique Users" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">Chat data loading...</div>
          )}
        </ChartCard>

        {/* Paywall funnel */}
        <ChartCard title="Paywall Funnel (30d)">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={funnelData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis type="category" dataKey="step" tick={{ fill: '#9ca3af', fontSize: 11 }} width={110} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {funnelData.map((_, i) => (
                  <Cell key={i} fill={['#FF9C15', '#f97316', '#22c55e', '#ef4444'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {paywall.bySource && Object.keys(paywall.bySource).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Views by Source:</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(paywall.bySource).map(([source, count]) => (
                  <span key={source} className="text-xs text-gray-400">
                    {source.replace(/_/g, ' ')}: <span className="text-white font-medium">{count as number}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {paywall.byPlan && Object.keys(paywall.byPlan).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Checkouts by Plan:</p>
              <div className="flex gap-4">
                {Object.entries(paywall.byPlan).map(([plan, count]) => (
                  <span key={plan} className="text-xs text-gray-400">
                    {plan}: <span className="text-white font-medium">{count as number}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Onboarding step dropoff */}
      {onboarding.stepDropoff && onboarding.stepDropoff.length > 0 && (
        <ChartCard title="Onboarding Step Dropoff">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={onboarding.stepDropoff.map((s: any) => ({ step: `${s.type} Step ${s.step}`, users: s.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="step" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="users" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Daily limit hits chart */}
      {dailyLimits.daily && dailyLimits.daily.length > 0 && (
        <ChartCard title="Daily Scan Limit Hits (30d)">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={dailyLimits.daily.map((d: any) => ({ date: formatDate(d.date), count: d.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Top scanned items */}
      {topScanned.length > 0 && (
        <ChartCard title="Top Scanned Items">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
            {topScanned.map((item: any, i: number) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-gray-500 w-6 text-right">{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-white">{item.itemName}</p>
                    <p className="text-xs text-gray-400">{item.mode}</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-white">{formatNumber(item.count)}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}

      {/* My Health */}
      <h2 className="text-lg font-bold text-white mt-8 mb-4 flex items-center gap-2">
        <span className="text-xl">&#x1F3E5;</span> My Health (30d)
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Setup Started" value={myHealth.setupStarts || 0} />
        <StatCard title="Setup Completed" value={myHealth.setupCompletes || 0} subtitle={myHealth.setupStarts > 0 ? `${((myHealth.setupCompletes / myHealth.setupStarts) * 100).toFixed(1)}% completion` : ''} />
        <StatCard title="Daily Opens" value={myHealth.dailyOpens || 0} subtitle={`${myHealth.dailyOpenUsers || 0} unique users`} />
        <StatCard title="Feature Interactions" value={myHealth.featureUses || 0} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <StatCard title="Medication Taken" value={myHealth.medicationTaken || 0} subtitle={`${myHealth.medicationTakenUsers || 0} unique users`} />
        <StatCard title="Reminders Set" value={myHealth.remindersSet || 0} subtitle={`${myHealth.reminderUsers || 0} unique users`} />
      </div>

      {/* Health feature breakdown */}
      {myHealth.byFeature && Object.keys(myHealth.byFeature).length > 0 && (
        <ChartCard title="Health Feature Usage (30d)" className="mt-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={Object.entries(myHealth.byFeature).map(([f, c]) => ({ feature: f.replace('_', ' '), count: c as number }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="feature" tick={{ fill: '#999', fontSize: 12 }} />
              <YAxis tick={{ fill: '#999', fontSize: 12 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
              <Bar dataKey="count" fill="#7B61FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Health medication taken daily */}
      {myHealth.medicationTakenDaily && myHealth.medicationTakenDaily.length > 0 && (
        <ChartCard title="Medication Doses Taken (Daily, 30d)" className="mt-4">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={myHealth.medicationTakenDaily.map((d: any) => ({ date: formatDate(d.date), count: d.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" tick={{ fill: '#999', fontSize: 11 }} />
              <YAxis tick={{ fill: '#999', fontSize: 12 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
              <Area type="monotone" dataKey="count" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Palm Kundli */}
      <h2 className="text-lg font-bold text-white mt-8 mb-4 flex items-center gap-2">
        <span className="text-xl">&#x1F52E;</span> Palm Kundli (30d)
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Profiles Created" value={palmKundli.profilesCreated || 0} subtitle={`${palmKundli.profileCreators || 0} users, avg ${palmKundli.avgProfilesPerUser || 0}/user`} />
        <StatCard title="Daily Opens" value={palmKundli.dailyOpens || 0} subtitle={`${palmKundli.dailyOpenUsers || 0} unique users`} />
        <StatCard title="Palm Scans" value={palmKundli.palmScanCompletes || 0} subtitle={`${palmKundli.palmScanUsers || 0} users, ${palmKundli.palmScanStarts || 0} started`} />
        <StatCard title="Chat Messages" value={palmKundli.chatMessages || 0} subtitle={`${palmKundli.chatUsers || 0} unique users`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <StatCard title="Compatibility Checks" value={palmKundli.compatibilityChecks || 0} subtitle={`${palmKundli.compatibilityUsers || 0} unique users`} />
        <StatCard title="Feature Interactions" value={palmKundli.featureUses || 0} />
      </div>

      {/* Kundli feature & language breakdown side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {palmKundli.byFeature && Object.keys(palmKundli.byFeature).length > 0 && (
          <ChartCard title="Kundli Tab Usage">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={Object.entries(palmKundli.byFeature).map(([f, c]) => ({ tab: f, count: c as number }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="tab" tick={{ fill: '#999', fontSize: 12 }} />
                <YAxis tick={{ fill: '#999', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {palmKundli.byLanguage && Object.keys(palmKundli.byLanguage).length > 0 && (
          <ChartCard title="Language Distribution">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={Object.entries(palmKundli.byLanguage).map(([lang, count]) => ({ name: lang, value: count as number }))}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                  paddingAngle={3} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {Object.keys(palmKundli.byLanguage).map((_: string, i: number) => (
                    <Cell key={i} fill={['#8B5CF6', '#6366F1', '#3B82F6', '#14B8A6', '#F59E0B', '#EF4444', '#EC4899', '#10B981', '#F97316', '#6B7280'][i % 10]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>
    </div>
  );
};

export default AiLensTab;
